/**
 * Claims currently occupying a finding's active slot.
 *
 * Same ordering rule as the queue, and for the same reason: a claim sitting in
 * `evidence_pending` while its window runs down is exactly as lost as one that
 * was never opened. Filing does not stop the clock — only TikTok receiving the
 * ticket does, and until the status says so the deadline still applies.
 */

import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { getClaims } from '../api.js';
import { useAsync } from '../useAsync.js';
import { formatDate, formatMinor, NO_VALUE, pluralize } from '../format.js';
import { sortByUrgency } from '../urgency.js';
import {
  Badge,
  EmptyState,
  ErrorCallout,
  Loading,
  StatusBadge,
  tierClass,
  UrgencyCell,
} from '../ui.js';

export function ClaimsPage(): ReactNode {
  const { data, error, loading, reload } = useAsync(() => getClaims(), []);
  const claims = useMemo(() => sortByUrgency(data?.claims ?? []), [data]);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Open claims</h1>
          <p>
            {pluralize(claims.length, 'claim')} still live. Terminal claims — paid, abandoned,
            expired — leave this list and release their finding.
          </p>
        </div>
        <button className="btn" onClick={reload} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      <section className="panel">
        {loading && !data ? <Loading label="Loading open claims" rows={4} /> : null}
        {error ? (
          <div className="panel-body">
            <ErrorCallout error={error} />
          </div>
        ) : null}

        {data && claims.length === 0 ? (
          <EmptyState title="No open claims">
            Nothing has been opened from the queue yet, or everything opened has reached a terminal
            state.
          </EmptyState>
        ) : null}

        {claims.length > 0 ? (
          <div className="table-scroll">
            <table>
              <caption className="sr-only">
                Open claims, ordered by the time remaining on the underlying finding.
              </caption>
              <thead>
                <tr>
                  <th scope="col">Time left</th>
                  <th scope="col">Claim</th>
                  <th scope="col">Status</th>
                  <th scope="col">Ticket</th>
                  <th scope="col" className="col-num">
                    Value
                  </th>
                  <th scope="col" className="col-tight">
                    Expires
                  </th>
                  <th scope="col">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {claims.map((claim) => (
                  <tr key={claim.id} className={tierClass(claim.daysLeft)}>
                    <td>
                      <UrgencyCell daysLeft={claim.daysLeft} expiresAt={claim.expiresAt} />
                    </td>
                    <td>
                      <Badge tone="code">{claim.claimCode}</Badge>
                      {claim.refileCount > 0 ? (
                        <div className="cell-detail">
                          refiled {pluralize(claim.refileCount, 'time')}
                        </div>
                      ) : null}
                    </td>
                    <td>
                      <StatusBadge status={claim.status} />
                      <div className="cell-detail">
                        {pluralize(claim.available.length, 'action')} available
                      </div>
                    </td>
                    <td className="mono">
                      {claim.ticketId ?? <span className="faint">unfiled</span>}
                    </td>
                    <td className="col-num">{formatMinor(claim.valueMinor)}</td>
                    <td className="col-num col-tight">
                      {claim.expiresAt ? formatDate(claim.expiresAt) : NO_VALUE}
                    </td>
                    <td className="col-tight">
                      <Link className="btn btn-sm" to={`/ops/claims/${claim.id}`}>
                        Open
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
    </>
  );
}
